import type { GetServerSideProps } from 'next'
import { post, httpDelete, put } from 'utils/request'
import { Modal, Form, Input, message, Popconfirm, Switch, InputNumber, Popover, Dropdown, Select, Radio, Divider } from 'antd'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import useAdmin from '@/hooks/swr/useAdmin'
import { useChannel } from '@/hooks/useWebSocket'
import ActionsLayout from '@/components/MatchLayout'
import { useMatch } from '@/hooks/swr/useMatch'
import XLSX from 'xlsx'
import { ColumnsType } from 'antd/es/table'
import { findIndex, random, orderBy, uniqBy, set, clone, max, sum } from 'lodash-es'
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  ExportOutlined,
  MessageOutlined,
  PlusOutlined,
  ReloadOutlined,
  TeamOutlined,
  UploadOutlined,
  TableOutlined,
  AppstoreOutlined,
  FileTextOutlined,
} from '@ant-design/icons'
import WechatShare from '@/components/WechatShare'
import produce from 'immer'
import useLogin from '@/hooks/swr/useLogin'
import { usePlayers } from '@/hooks/swr/usePlayer'
import Button from '@/components/Button'
import { debouncedFunction } from '@/utils/debouncedFunction'
import { playerType } from '@/utils/types'
import { ItemType } from 'antd/es/menu/interface'
import Image from 'next/image'
import useClubMembers from '@/hooks/swr/useClubMembers'
import ColumnResizeTable from '@/components/ColumnResizeTable'
import { useAllRounds } from '@/hooks/swr/useRound'
import { CopyToClipboard } from 'react-copy-to-clipboard'

type PropType = { match_id: string }

const TEST_PLAYER_COUNT = 20
const TEST_PLAYER_NAMES = [
  '张伟',
  '王伟',
  '李伟',
  '刘伟',
  '陈伟',
  '杨伟',
  '赵伟',
  '黄伟',
  '周伟',
  '吴伟',
  '徐伟',
  '孙伟',
  '马伟',
  '胡伟',
  '郭伟',
  '何伟',
  '高伟',
  '林伟',
  '罗伟',
  '梁伟',
  '宋伟',
  '郑伟',
  '谢伟',
  '唐伟',
  '许伟',
  '邓伟',
  '冯伟',
  '韩伟',
  '曹伟',
  '曾伟',
  '王芳',
  '李芳',
  '张敏',
  '刘敏',
  '陈敏',
  '杨敏',
  '赵敏',
  '黄敏',
  '周敏',
  '吴敏',
  '徐敏',
  '孙敏',
  '马敏',
  '胡敏',
  '郭敏',
  '何敏',
  '高敏',
  '林敏',
  '罗敏',
  '梁敏',
  '宋敏',
  '郑敏',
  '谢敏',
  '唐敏',
  '许敏',
  '邓敏',
  '冯敏',
  '韩敏',
  '曹敏',
  '曾敏',
]

const getTestNames = (count: number, existingNames: Set<string>) => {
  const result: string[] = []
  for (let i = 0; result.length < count; i += 1) {
    const base = TEST_PLAYER_NAMES[i % TEST_PLAYER_NAMES.length]
    const round = Math.floor(i / TEST_PLAYER_NAMES.length)
    const candidate = round > 0 ? `${base}${round + 1}` : base
    if (!existingNames.has(candidate)) {
      result.push(candidate)
    }
  }
  return result
}

const sorter = (a, b) => {
  let sortNum = 0

  // 按签到状态排序
  if (a.check_in && !b.check_in) {
    sortNum = 1
  }
  if (!a.check_in && b.check_in) {
    sortNum = -1
  }

  // 按弃权状态排序
  if (!a.give_up && b.give_up) {
    sortNum = 1
  }
  if (a.give_up && !b.give_up) {
    sortNum = -1
  }

  // 如果签到和弃权状态相同，按名字排序
  if (sortNum === 0) {
    sortNum = a.name.localeCompare(b.name)
  }

  return sortNum
}

const Players: Next.PageWithLayout<PropType> = ({ match_id, is_presentation }) => {
  const [newPlayerModalOpen, setNewPlayerModalOpen] = useState<boolean>(false)
  const [batchRenameModalOpen, setBatchRenameModalOpen] = useState<boolean>(false)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [importModalOpen, setImpoertModalOpen] = useState(false)
  const [importLoading, setImportLoading] = useState(false)
  const [importText, setImportText] = useState('')
  const [textImportExpanded, setTextImportExpanded] = useState(false)
  const [batchSmsModal, setBatchSmsModal] = useState<{ open: boolean; variables: string[]; loading: boolean }>({
    open: false,
    variables: ['', '', ''],
    loading: false,
  })
  const [clubImportModalOpen, setClubImportModalOpen] = useState(false)
  const [clubMembersVisible, setClubMembersVisible] = useState(false)
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list')
  const { data: allRounds } = useAllRounds(match_id)
  const excelFileAccept = '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

  const onGoingRounds = useMemo(() => (allRounds ?? []).filter((r) => r.status !== 'finished'), [allRounds])

  const isPlayerArranged = useCallback(
    (playerId: string) => {
      return onGoingRounds
        .map((r) => [r.player1_id, r.player2_id, r.player3_id, r.player4_id])
        .flat()
        .includes(playerId)
    },
    [onGoingRounds]
  )

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedViewMode = localStorage.getItem('playersOrTeamsViewMode')
      if (savedViewMode === 'list' || savedViewMode === 'card') {
        setViewMode(savedViewMode)
      }
    }
  }, [])

  const handleViewModeChange = (mode: 'list' | 'card') => {
    setViewMode(mode)
    if (typeof window !== 'undefined') {
      localStorage.setItem('playersOrTeamsViewMode', mode)
    }
  }

  const { isAdmin } = useAdmin(match_id)
  const { current_user } = useLogin()
  const [order, setOrder] = React.useState('')

  const getComparator = useCallback(
    (order) => (a, b) => {
      // 按姓名排序:相同/相近姓名相邻,便于发现重复报名(按中文拼音)
      if (order === 'name') {
        return (a.name ?? '').localeCompare(b.name ?? '', 'zh-Hans-CN')
      }
      const baseSort = sorter(a, b)
      if (order === 'desc') {
        return baseSort
      }
      if (order === 'asc') {
        return -baseSort
      }
      return 0 // 默认不排序
    },
    []
  )
  const [editPlayerInfo, setEditPlayerInfo] = useState<playerType>(undefined)

  const { data: match, mutate: mutateMatch } = useMatch(match_id)
  const { data: players, mutate: mutatePlayers } = usePlayers(match_id)
  const { data: club_members } = useClubMembers(match.club)
  const [filterKeywords, setFilterKeywords] = useState('')

  const sortedPlayers = useMemo(() => {
    const filteredPlayers = (players ?? []).filter((p) => p.mobile?.includes(filterKeywords) || p.name?.includes(filterKeywords))
    // created_at 按解析后的时间戳排,而不是按字符串:列表来自 Go、WS 回推来自 Rails,两者时间格式不同,
    // 字符串比较会让刚更新的选手跳到顶部(每点一次性别就重排一次)
    return orderBy(filteredPlayers, [is_presentation && 'check_in', (p: any) => Date.parse(p.created_at) || 0])
  }, [filterKeywords, is_presentation, players])
  const members = useMemo(() => players.flatMap((player) => [{ mobile: player.mobile, name: player.name }]).filter((member) => (member.mobile ?? '').length > 0), [players])

  const postBatchSms = useCallback((params) => {
    post('matches/send_sms_notifications', { sms_params: params }).then(() => {
      message.success('短信群发成功')
      setBatchSmsModal({ open: false, variables: ['', '', ''], loading: false })
    })
  }, [])

  const sortByOrder = (teams, comparator) => {
    const sortTeams = teams.map((v, index) => [v, index])
    sortTeams.sort((a, b) => {
      const order = comparator(a[0], b[0])
      if (order !== 0) {
        return order
      }
      return a[1] - b[1]
    })
    return sortTeams.map((v) => v[0])
  }

  useChannel(`user-to-match-${match_id}`, true, (obj) => {
    switch (obj.action) {
      case 'updatePlayerCheckIn':
        mutatePlayers(
          (cachedPlayers) =>
            produce(cachedPlayers, (draft) => {
              const i = findIndex(draft, (player) => player.id === obj.data.player_id)
              if (i >= 0) {
                draft[i].check_in = true
              }
              return draft
            }),
          false
        )
        break
      default:
    }
  })

  useChannel(`match-${match_id}`, true, (obj) => {
    switch (obj.action) {
      case 'createPlayer':
        mutatePlayers((cachedPlayers) => uniqBy([...cachedPlayers, obj.data.player], (p) => p.id), false)
        break
      case 'deletePlayer':
        mutatePlayers((cachedPlayers) => cachedPlayers.filter((team) => team.id !== obj.data.player_id), false)
        break
      case 'updatePlayer':
        mutatePlayers(
          (cachedPlayers) =>
            produce(cachedPlayers, (draft) => {
              const i = findIndex(draft, (team) => team.id === obj.data.player.id)
              if (i >= 0) {
                // 合并而非整条替换,并保留列表原有的 created_at(Go 格式),避免 Rails 格式混入导致排序漂移
                draft[i] = { ...draft[i], ...obj.data.player, created_at: draft[i].created_at }
              }
              return draft
            }),
          false
        )
        break
      case 'mutatePlayers':
        mutatePlayers()
        break

      default:
    }
  })

  const postNewPlayer = useCallback(
    (value) => {
      post('users/players', { match_id, ...value }).then((res) => {
        if (res.ok) {
          setNewPlayerModalOpen(false)
        } else {
          message.info(res.message)
        }
        mutatePlayers()
      })
    },
    [match_id, mutatePlayers]
  )

  const batchPostNewPlayer = useCallback(
    (players: playerType[]) => {
      post('users/players/batch_create', { players, match_id }).then((res) => {
        setImportLoading(false)
        if (res.ok) {
          setImpoertModalOpen(false)
          message.info(`成功导入${res.new_players.length}人，过滤重复选手${res.repeated_players_count}人`, 10)
          mutatePlayers()
        } else {
          message.info(res.message)
        }
      })
    },
    [match_id, mutatePlayers]
  )
  const batchRename = useCallback(
    (start_number) => {
      post('users/players/batch_rename', { match_id, start_number }).then((res) => {
        if (res.ok) {
          mutatePlayers()
        } else {
          message.info(res.message)
        }
      })
    },
    [match_id, mutatePlayers]
  )

  const deletePlayer = useCallback(
    (delete_team_id: string) => {
      if (isPlayerArranged(delete_team_id)) {
        message.error('已编排的选手不能删除')
        return Promise.reject()
      }
      return httpDelete(`users/players/${delete_team_id}`, { match_id }).then((res) => {
        if (res.ok) {
          mutatePlayers()
        } else {
          message.error(res.message, 10)
        }
      })
    },
    [match_id, mutatePlayers, isPlayerArranged]
  )

  const putPlayer = useCallback(
    (value) => {
      const trimedValue = { ...value, name: value.name.trim() }
      put(`users/players/${editPlayerInfo?.id}`, trimedValue).then(() => {
        mutatePlayers()
        setEditPlayerInfo(undefined)
      })
    },
    [editPlayerInfo?.id, mutatePlayers]
  )

  // 「显示列·性别」开关:按比赛持久化 show_gender(有的比赛不需要性别,默认关);matches#update 是整体覆盖 data,须带全量
  const toggleShowGender = useCallback(
    (checked: boolean) => {
      put(`matches/${match_id}`, { data: { ...(match?.data || {}), show_gender: checked } }).then((res) => {
        if (res?.ok === false) {
          message.error(res.message || '操作失败')
          return
        }
        mutateMatch()
      })
    },
    [match?.data, match_id, mutateMatch]
  )

  // 性别标记:颁奖前快速区分女选手(最佳女选手/女双/混双)。再点已选中的=清除。
  // 乐观更新:点击即就地改本地缓存里这一条,不整表重取——整表重取会让服务端返回顺序与页面排序短暂不一致,
  // 该行先按服务端位置画一帧再排回来,整行上下猛跳(实测位移 ~82px)。请求失败才回滚重取。
  const setGender = useCallback(
    (player: playerType, gender: string) => {
      const next = player.gender === gender ? '' : gender
      const patch = (p: any) => (p?.id === player.id ? { ...p, gender: next } : p)
      mutatePlayers((cur: any) => {
        if (Array.isArray(cur)) return cur.map(patch)
        if (cur && Array.isArray(cur.players)) return { ...cur, players: cur.players.map(patch) }
        return cur
      }, false)
      put(`users/players/${player.id}`, { gender: next }).then((res) => {
        if (res?.ok === false) {
          message.error(res.message || '操作失败')
          mutatePlayers()
        }
      })
    },
    [mutatePlayers]
  )

  // 一键「离场」:复用 give_up 标记。离场后一键编排/沿用上一轮均自动跳过该选手,过往成绩保留。
  const toggleLeave = useCallback(
    (player: playerType) => {
      put(`users/players/${player.id}`, { give_up: !player.give_up }).then((res) => {
        if (res?.ok === false) {
          message.error(res.message || '操作失败')
          return
        }
        message.success(player.give_up ? '已恢复参赛' : '已标记离场，后续编排将自动跳过')
        mutatePlayers()
      })
    },
    [mutatePlayers]
  )

  const onGenerateTestPlayers = useCallback(() => {
    if (!match?.is_test_match) {
      message.info('仅测试赛可用')
      return
    }
    const existingNames = new Set((players ?? []).map((player) => player.name).filter((name) => (name ?? '').length > 0))
    const names = getTestNames(TEST_PLAYER_COUNT, existingNames)
    const existingIndices = (players ?? [])
      .map((player) => parseInt(player.index, 10))
      .filter((value) => !Number.isNaN(value))
    const startIndex = (existingIndices.length ? Math.max(...existingIndices) : 0) + 1
    const newPlayers: playerType[] = Array.from({ length: TEST_PLAYER_COUNT }).map((_, index) => ({
      index: `${startIndex + index}号`,
      name: names[index] ?? `测试选手${startIndex + index}`,
      mobile: '',
      guild: '',
    })) as playerType[]
    batchPostNewPlayer(newPlayers)
  }, [batchPostNewPlayer, match?.is_test_match, players])

  const onBatchCheckIn = useCallback(() => {
    put(`users/players/batch_check_in`, { match_id }).then((res) => {
      if (res.ok) {
        message.info('一键签到成功')
        mutatePlayers()
      }
    })
  }, [match_id, mutatePlayers])

  const onBatchCheckOut = useCallback(() => {
    put(`users/players/batch_check_out`, { match_id }).then((res) => {
      if (res.ok) {
        message.info('一键取消签到成功')
        mutatePlayers()
      }
    })
  }, [match_id, mutatePlayers])

  const onResetAllScore = useCallback(() => {
    put(`matches/reset_all_scores`, { match_id }).then((res) => {
      if (res.ok) {
        message.info('重置分数成功')
        mutatePlayers()
      } else {
        message.error(res.message)
      }
    })
  }, [match_id, mutatePlayers])

  const onBatchDelete = useCallback(() => {
    httpDelete(`users/players/batch_delete`, { match_id }).then((res) => {
      if (res.ok) {
        mutatePlayers()
        message.success('删除成功')
      } else {
        message.error(res.message)
      }
    })
  }, [match_id, mutatePlayers])

  const exportPlayerExampleExcel = useCallback(() => {
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet([], {
      header: ['姓名', '手机', '团体', '性别'],
    })
    XLSX.utils.book_append_sheet(workbook, worksheet, 'sheet1')
    XLSX.write(workbook, {
      sheet: 'sheet1',
      type: 'base64',
      cellDates: true,
      bookType: 'xlsx',
    })

    if (workbook) {
      XLSX.writeFile(workbook, `参赛选手录入表.xlsx`)
    } else {
      message.info('出错了')
    }
  }, [])

  const exportPlayerInfo = useCallback(() => {
    const workbook = XLSX.utils.book_new()

    const teamJson = sortedPlayers.map((team) => ({
      编号: team.index,
      姓名: team.name,
      手机: team.mobile,
      团体: team.guild,
      性别: team.gender ?? '',
    }))

    const worksheet = XLSX.utils.json_to_sheet(teamJson, {
      header: ['编号', '姓名', '手机', '团体', '性别'],
    })

    XLSX.utils.book_append_sheet(workbook, worksheet, 'sheet1')
    XLSX.write(workbook, {
      sheet: 'sheet1',
      type: 'base64',
      cellDates: true,
      bookType: 'xlsx',
    })

    if (workbook) {
      XLSX.writeFile(workbook, `${match?.name}选手信息表.xlsx`)
    } else {
      message.info('出错了')
    }
  }, [match?.name, sortedPlayers])

  const onImportPlayersExcel = useCallback(
    (event, useExcelIndex = false) => {
      setImportLoading(true)
      const file = event?.target?.files?.[0]
      if (!file) {
        setImportLoading(false)
        return
      }
      const reader = new FileReader()
      try {
        reader.readAsArrayBuffer(file)
        reader.onload = () => {
          const workbook = XLSX.read(reader.result)
          let ExcelData: object[] = XLSX.utils.sheet_to_json(workbook.Sheets.sheet1)
          if (!ExcelData || ExcelData.length === 0) {
            ExcelData = XLSX.utils.sheet_to_json(workbook.Sheets['选手信息'])
          }
          if (!ExcelData || ExcelData.length === 0) {
            ExcelData = XLSX.utils.sheet_to_json(workbook.Sheets['队伍信息'])
          }
          ExcelData = ExcelData.filter((teamObject) => teamObject['姓名'])
          const newPlayers: playerType[] = ExcelData.map((teamObject, index) => {
            const excelIndex = useExcelIndex ? teamObject['编号'] ?? teamObject['序号'] ?? '' : ''
            return {
              index: excelIndex ? `${excelIndex}` : `${index + 1}号`,
              name: teamObject['姓名'],
              mobile: teamObject['手机'] ?? '',
              guild: teamObject['团体'] ?? '',
              gender: teamObject['性别'] ?? '',
            }
          }) as playerType[]
          if (newPlayers.length > 0) {
            batchPostNewPlayer(newPlayers)
          } else {
            setImportLoading(false)
            message.info('请检查是否按照系统格式填写表格')
          }
        }
        if (event?.target) {
          event.target.value = ''
        }
      } catch (error) {
        console.error(error)
        setImportLoading(false)
      }
    },
    [batchPostNewPlayer]
  )

  const excelImportOptions = useMemo(
    () => [
      {
        key: 'auto',
        title: '自动生成编号',
        description: '无需提前准备编号，系统会按照导入顺序生成“1号、2号...”',
        helper: '最适合第一次导入名单',
        badge: '推荐',
        icon: <AppstoreOutlined className="text-blue-600" />,
        buttonText: '上传Excel',
        handler: (e) => onImportPlayersExcel(e),
        highlight: true,
        showTemplateLink: true,
      },
      {
        key: 'respect',
        title: '沿用表格中的编号',
        description: 'Excel 中的编号会被完整保留，适合已整理好编号的名单。',
        helper: '系统会读取“编号”列',
        icon: <TableOutlined className="text-blue-500" />,
        buttonText: '使用Excel编号',
        handler: (e) => onImportPlayersExcel(e, true),
        highlight: false,
      },
    ],
    [onImportPlayersExcel]
  )
  const [primaryExcelOption, ...secondaryExcelOptions] = excelImportOptions
  const renderExcelOptionCard = useCallback(
    (option) => (
      <div key={option.key} className={`rounded-2xl border p-4 shadow-sm ${option.highlight ? 'border-blue-200 bg-blue-50' : 'border-gray-100 bg-gray-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-base font-semibold text-gray-900">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full shadow ${option.highlight ? 'bg-blue-100' : 'bg-white'}`}>{option.icon}</div>
            {option.title}
          </div>
          {option.badge && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${option.highlight ? 'bg-blue-100 text-blue-700' : 'bg-blue-50 text-blue-600'}`}>{option.badge}</span>
          )}
        </div>
        <p className="mt-2 text-sm text-gray-600">{option.description}</p>
        <Button
          className={`relative mt-4 flex h-11 w-full items-center justify-center rounded-xl border-2 font-semibold ${
            option.highlight ? 'border-blue-500 text-blue-600 hover:border-blue-600 hover:text-blue-700' : 'border-gray-300 text-gray-700'
          }`}
        >
          <UploadOutlined className="mr-2 text-base" />
          {option.buttonText}
          <input type="file" className="absolute left-0 top-0 h-full w-full cursor-pointer opacity-0" accept={excelFileAccept} onChange={option.handler} />
        </Button>
        {option.showTemplateLink && (
          <Button type="link" className="mt-2 p-0 text-sm font-semibold text-blue-600 underline underline-offset-2" onClick={exportPlayerExampleExcel}>
            下载Excel样例表
          </Button>
        )}
        <div className="mt-2 text-xs text-gray-400">{option.helper}</div>
      </div>
    ),
    [excelFileAccept, exportPlayerExampleExcel]
  )

  const wechatShare = useMemo(() => {
    if (match.name) {
      return <WechatShare res={{ title: match.name }} />
    }
    return null
  }, [match.name])

  const importFromClubMembers = useCallback(
    (club) => {
      post('club_members/import_players_from_club_members', { club, match_id }).then((res) => {
        if (res.ok) {
          mutatePlayers()
          message.info('成功')
        } else {
          message.info(res.message)
        }
      })
    },
    [match_id, mutatePlayers]
  )
  const importOnePlayerFromClubMember = useCallback(
    (member_id) => {
      post('club_members/import_one_player_from_club_member', { member_id, match_id }).then((res) => {
        if (res.ok) {
          mutatePlayers()
          message.info('成功')
        } else {
          message.info(res.message)
        }
      })
    },
    [match_id, mutatePlayers]
  )

  const OptionItems: ItemType[] = [
    {
      key: 'player-management',
      label: <div className="px-2 py-1 text-xs font-medium uppercase text-gray-500">选手管理</div>,
      type: 'group',
      children: [
        {
          key: '10',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => setNewPlayerModalOpen(true)}>
              <PlusOutlined className="mr-2" />
              <span>新增选手</span>
            </div>
          ),
        },
        {
          key: '20',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => setBatchRenameModalOpen(true)}>
              <EditOutlined className="mr-2" />
              <span>重新编号</span>
            </div>
          ),
        },
        {
          key: '30',
          label: (
            <div
              className="flex items-center rounded-md px-1 py-2 transition-colors"
              onClick={() =>
                Modal.confirm({
                  content: '您确定要一键全签到吗',
                  onOk: () => {
                    onBatchCheckIn()
                  },
                })
              }
            >
              <CheckOutlined className="mr-2" />
              <span>一键签到</span>
            </div>
          ),
        },
        {
          key: '32',
          label: (
            <div
              className="flex items-center rounded-md px-1 py-2 transition-colors"
              onClick={() =>
                Modal.confirm({
                  content: '您确定要一键全取消签到吗',
                  onOk: () => {
                    onBatchCheckOut()
                  },
                })
              }
            >
              <CloseOutlined className="mr-2" />
              <span>一键取消签到</span>
            </div>
          ),
        },
      ],
    },
    {
      key: 'export-import',
      label: <div className="px-2 py-1 text-xs font-medium uppercase text-gray-500">导出导入</div>,
      type: 'group',
      children: [
        {
          key: '40',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => setExportModalOpen(true)}>
              <ExportOutlined className="mr-2" />
              <span>导出选手信息</span>
            </div>
          ),
        },
        {
          key: '50',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => exportPlayerExampleExcel()}>
              <DownloadOutlined className="mr-2" />
              <span>导出样例表</span>
            </div>
          ),
        },
        current_user?.is_gd_admin && {
          key: '55',
          label: (
            <div
              className="flex items-center rounded-md px-1 py-2 transition-colors"
              onClick={() => {
                const arrangedPlayers = players?.filter((player) => isPlayerArranged(player.id)) ?? []
                if (arrangedPlayers.length > 0) {
                  Modal.info({
                    title: '无法批量删除',
                    content: (
                      <div>
                        <div>当前有 {arrangedPlayers.length} 个选手处于"已编排"状态。</div>
                        <div>请先在桌次编排中移除这些选手后再进行批量删除。</div>
                      </div>
                    ),
                    okText: '知道了',
                  })
                  return
                }
                Modal.confirm({
                  content: '您确定要全部删除吗',
                  type: 'warning',
                  onOk: () => {
                    onBatchDelete()
                  },
                })
              }}
            >
              <CloseOutlined className="mr-2" />
              <span>批量删除</span>
            </div>
          ),
        },
        {
          key: '60',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => setImpoertModalOpen(true)}>
              <UploadOutlined className="mr-2" />
              <span>批量导入</span>
            </div>
          ),
        },
        !!match.club && {
          key: '63',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => setClubImportModalOpen(true)}>
              <TeamOutlined className="mr-2" />
              <span>从俱乐部成员导入</span>
            </div>
          ),
        },
      ],
    },
    {
      key: 'admin-operations',
      label: <div className="px-2 py-1 text-xs font-medium uppercase text-gray-500">管理员操作</div>,
      type: 'group',
      children: [
        current_user.is_gd_admin && {
          key: '68',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => onResetAllScore()}>
              <ReloadOutlined className="mr-2" />
              <span>重置分数</span>
            </div>
          ),
        },
        current_user.is_gd_admin && {
          key: '70',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => setBatchSmsModal({ ...batchSmsModal, open: true })}>
              <MessageOutlined className="mr-2" />
              <span>群发短信</span>
            </div>
          ),
        },
        {
          key: '80',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => window.open(`/match/${match.id}/pay_batch_sms`)}>
              <MessageOutlined className="mr-2" />
              <span>付费群发短信</span>
            </div>
          ),
        },
      ],
    },
  ]

  const showPayment = useMemo(() => isAdmin && !is_presentation && match.entry_fee > 0, [isAdmin, is_presentation, match.entry_fee])

  const playerCard = useCallback(
    (player, index) => {
      return (
        <div key={player.id || index} className="w-full rounded-lg bg-white p-3 shadow-sm transition-all hover:shadow-md">
          <div className="flex items-start justify-between">
            <div className="flex items-center">
              <span className="mr-2 text-lg font-bold">{player.index}</span>
              {(() => {
                if (isPlayerArranged(player.id)) {
                  return <span className="inline-block rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-600">已编排</span>
                }
                let statusElement = <span className="inline-block rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-600">已签到</span>
                if (!player.check_in) {
                  statusElement = <span className="inline-block rounded-full bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-600">未签到</span>
                }
                if (player.give_up) {
                  statusElement = <span className="inline-block rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-600">已离场</span>
                }
                return statusElement
              })()}
            </div>
            {isAdmin && !is_presentation && (
              <div className="flex space-x-1">
                <Button size="small" danger={!player.give_up} onClick={() => toggleLeave(player)}>
                  {player.give_up ? '恢复' : '离场'}
                </Button>
                <Button size="small" type="primary" ghost onClick={() => setEditPlayerInfo(player)}>
                  编辑
                </Button>
                <Popconfirm
                  title={
                    <div className="flex flex-col items-center justify-center">
                      <div>删除后附带的比赛记录也会消失</div>
                      <div>谨慎删除</div>
                    </div>
                  }
                  onCancel={() => {}}
                  okText="确认"
                  cancelText="取消"
                  onConfirm={() => deletePlayer(player.id)}
                  disabled={isPlayerArranged(player.id)}
                >
                  <Button size="small" danger disabled={isPlayerArranged(player.id)}>
                    删除
                  </Button>
                </Popconfirm>
              </div>
            )}
          </div>

          <div className="mt-2 text-lg font-bold">{player.name}</div>

          <div className="mt-1 flex flex-col text-sm">
            <div className="flex w-full items-center gap-1 truncate">
              <div>团体:</div>
              {player.guild && <div className="text-xs text-gray-600">{player.guild}</div>}
            </div>
            {showPayment && (
              <div className="mt-1 font-medium">{player.tokens_amount > 0 ? `${player.tokens_amount}点` : player.has_paid ? `${player.payment_amount / 100}元` : '无支付'}</div>
            )}
            {!!match.club && !club_members.find((m) => m.mobile === player.mobile) && <div className="mt-1 text-xs text-yellow-600">(非俱乐部)</div>}
          </div>
        </div>
      )
    },
    [club_members, deletePlayer, isAdmin, is_presentation, isPlayerArranged, match.club, showPayment]
  )

  const tableRows = useMemo(() => {
    return sortByOrder(sortedPlayers, getComparator(order))
  }, [getComparator, order, sortedPlayers])

  const createPlayersComponent = useCallback(() => {
    return (
      <div>
        <div className="flex flex-col items-center">
          <div className="mt-10 text-3xl font-bold text-white">暂无选手</div>
          <div className="text-semibold mt-4 font-bold text-white">下载样例表即可一键导入</div>
          <Image alt="下载样例表" src="/import-teams.jpg" width={320} height={200} />
        </div>
        <div className="flex justify-center">
          <Button
            className="mt-1 h-16 w-60 rounded-xl bg-gray-100 text-xl font-semibold text-primary"
            onClick={() => {
              setNewPlayerModalOpen(true)
            }}
          >
            手动新增一人
          </Button>
          {match?.is_test_match && (
            <Button
              className="ml-10 mt-1 h-16 w-60 rounded-xl bg-white text-xl font-semibold text-primary"
              onClick={() =>
                Modal.confirm({
                  content: `确认生成约${TEST_PLAYER_COUNT}名测试选手吗？`,
                  onOk: () => onGenerateTestPlayers(),
                })
              }
            >
              一键生成测试选手
            </Button>
          )}
          <div className="ml-10 flex flex-col">
            <Button className="ml-1 mt-1 h-16 w-60 rounded-xl  border-none text-xl font-semibold text-primary" onClick={() => setImpoertModalOpen(true)}>
              批量导入选手
              <Image alt="下载样例表" src="/recommend-mark.jpg" width={40} height={40} className="absolute left-0 top-0" />
            </Button>
            <Button className="mt-1 h-fit w-60  text-xl text-white" onClick={() => exportPlayerExampleExcel()} type="link">
              <span className="border-0 border-b border-solid border-white font-semibold">下 载 样 例 表</span>
            </Button>
          </div>
        </div>
      </div>
    )
  }, [exportPlayerExampleExcel, match?.is_test_match, onGenerateTestPlayers])

  const exportText = useMemo(() => {
    let text = ''
    orderBy(players, 'created_at').forEach((t) => {
      text += `${t.index ?? ''}\t${t.name ?? ''}\t${t.mobile ?? ''}\t${t.guild ?? ''}\n`
    })
    return text
  }, [players])

  const columns = useMemo(() => {
    const basedColumns: ColumnsType<any> = [
      {
        title: '编号',
        dataIndex: 'index',
        key: 'index',
        width: 100,
      },
      {
        title: '姓名',
        dataIndex: 'name',
        key: 'name',
        width: 400,
        render: (_, player) => {
          return <div className="truncate font-bold">{player.name}</div>
        },
      },
      {
        title: '状态',
        key: 'status',
        width: 150,
        align: 'center',
        render: (_, player) => {
          if (isPlayerArranged(player.id)) {
            return <span className="inline-block rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-600">已编排</span>
          }
          if (player.give_up) {
            return <span className="inline-block rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-600">已离场</span>
          }
          if (player.check_in) {
            return <span className="inline-block rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-600">已签到</span>
          }
          return <span className="inline-block rounded-full bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-600">未签到</span>
        },
      },
      {
        title: '团体',
        dataIndex: 'guild',
        key: 'guild',
        width: 300,
        render: (_, player) => {
          return <div className="truncate">{player.guild}</div>
        },
      },
    ]
    // 性别列按比赛开关(show_gender)显示。选中态:全局 CSS 有 `.ant-btn-default{background:#f5f5f5!important}`
    // 会压掉内联/Tailwind 背景色,故选中时切成 type=primary(不被该规则匹配)再用内联 style 上粉/蓝
    if (match?.show_gender) {
      basedColumns.push({
        title: '性别',
        key: 'gender',
        width: 110,
        align: 'center',
        render: (_, player) => (
          <div className="flex justify-center gap-1">
            <Button size="small" shape="round" className={`gender-btn${player.gender === '女' ? ' gender-on-female' : ''}`} onClick={() => setGender(player, '女')}>
              ♀
            </Button>
            <Button size="small" shape="round" className={`gender-btn${player.gender === '男' ? ' gender-on-male' : ''}`} onClick={() => setGender(player, '男')}>
              ♂
            </Button>
          </div>
        ),
      })
    }
    if (showPayment) {
      basedColumns.push({
        title: '支付',
        key: 'payment',
        width: 120,
        render: (_, player) => {
          if (player.tokens_amount > 0) {
            return <span>{player.tokens_amount}点</span>
          }
          if (player.has_paid) {
            return <span>{player.payment_amount / 100}元</span>
          }
          return <span className="text-gray-500">无支付</span>
        },
      })
    }
    if (isAdmin || is_presentation) {
      basedColumns.push({
        title: '操作',
        key: 'action',
        fixed: 'right',
        width: 168,
        render: (_, player) => {
          return (
            <div className="flex space-x-1">
              <Button size="small" danger={!player.give_up} onClick={() => toggleLeave(player)}>
                {player.give_up ? '恢复' : '离场'}
              </Button>
              <Button size="small" type="primary" ghost onClick={() => setEditPlayerInfo(player)}>
                编辑
              </Button>
              <Popconfirm
                title={
                  <div className="flex flex-col items-center justify-center">
                    <div>删除后附带的比赛记录也会消失</div>
                    <div>谨慎删除</div>
                  </div>
                }
                onCancel={() => {}}
                okText="确认"
                cancelText="取消"
                onConfirm={() => deletePlayer(player.id)}
                disabled={isPlayerArranged(player.id)}
              >
                <Button size="small" danger disabled={isPlayerArranged(player.id)}>
                  删除
                </Button>
              </Popconfirm>
            </div>
          )
        },
      })
    }
    if (window?.innerWidth > 500) {
      basedColumns.push({
        title: '',
        dataIndex: '',
        key: 'hidden',
        fixed: 'right',
        width: 0,
      })
    }
    return basedColumns
  }, [deletePlayer, isAdmin, is_presentation, isPlayerArranged, showPayment, match?.show_gender, setGender])

  const maxPlayerIndex = useMemo(() => {
    return max([0, ...sortedPlayers.map((t) => parseInt(t.index, 10))])
  }, [sortedPlayers])

  return (
    <div className="h-full w-full">
      {wechatShare}
      <div className="sticky top-6 z-40 mb-6 w-full px-2 lg:px-6">
        <div className="flex w-full flex-wrap items-center justify-between gap-3 rounded-lg bg-stone-800 px-4 py-3 shadow-md lg:py-1">
          <div className="text-xl font-bold text-white md:text-2xl">选手管理</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input.Search
              placeholder="搜索手机号或名字"
              enterButton={<span className="flex items-center">搜索</span>}
              allowClear
              className="w-full rounded-md md:w-60"
              size="middle"
              onSearch={(value) => {
                setFilterKeywords(value)
              }}
            />
            <div className="grid grid-cols-3 gap-2 md:w-auto md:grid-cols-6">
              <Select
                className="w-full rounded-md md:w-32"
                size="middle"
                value={order}
                onChange={(value) => setOrder(value)}
                options={[
                  { label: '默认顺序', value: '' },
                  { label: '已签到', value: 'desc' },
                  { label: '未签到', value: 'asc' },
                  { label: '按姓名(查重)', value: 'name' },
                ]}
              />
              <Popover
                content={
                  <div className="p-2">
                    <div className="mb-2 font-medium text-gray-700">选手统计</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      <div className="text-sm text-gray-600">已签到:</div>
                      <div className="font-medium text-green-600">{sortedPlayers.filter((t) => t.check_in && !t.give_up).length}</div>
                      <div className="text-sm text-gray-600">未签到:</div>
                      <div className="font-medium text-yellow-600">{sortedPlayers.filter((t) => !t.check_in && !t.give_up).length}</div>
                      <div className="text-sm text-gray-600">已离场:</div>
                      <div className="font-medium text-red-600">{sortedPlayers.filter((t) => t.give_up).length}</div>
                    </div>

                    <div className="my-2 font-medium text-gray-700">支付统计</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">金额: {sum(players.map((t) => Number(t.payment_amount) || 0)) / 100}元</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">点数: {sum(players.map((t) => Number(t.tokens_amount) || 0))}点</div>
                  </div>
                }
                placement="bottom"
                trigger="click"
              >
                <Button className="w-full md:w-auto" icon={<TeamOutlined />}>
                  数量统计
                </Button>
              </Popover>
              <Button
                className="w-full bg-gray-100 text-primary md:w-auto"
                onClick={() => {
                  setNewPlayerModalOpen(true)
                }}
              >
                手动新增一人
              </Button>
              {match?.is_test_match && (
                <Button
                  className="w-full bg-white text-primary md:w-auto"
                  onClick={() =>
                    Modal.confirm({
                      content: `确认生成约${TEST_PLAYER_COUNT}名测试选手吗？`,
                      onOk: () => onGenerateTestPlayers(),
                    })
                  }
                >
                  一键生成测试选手
                </Button>
              )}
              <Button
                className="w-full md:w-auto"
                icon={viewMode === 'card' ? <TableOutlined /> : <AppstoreOutlined />}
                onClick={() => handleViewModeChange(viewMode === 'card' ? 'list' : 'card')}
              >
                {viewMode === 'card' ? '表格视图' : '卡片视图'}
              </Button>
              {isAdmin && (
                <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1 text-sm text-gray-600" title="有的比赛不需要性别;开启后选手列表显示性别列,结果排名/小程序显示♀标记">
                  <span>显示列</span>
                  <Switch size="small" checked={!!match?.show_gender} onChange={toggleShowGender} />
                  <span>性别</span>
                </div>
              )}
              {isAdmin ? (
                <Dropdown menu={{ items: OptionItems }} trigger={['click']} dropdownRender={(menu) => <div className="min-w-[220px] rounded-lg bg-white shadow-lg">{menu}</div>}>
                  <Button className="flex items-center justify-center" type="primary">
                    <span>操作</span>
                    <DownOutlined className="ml-1" />
                  </Button>
                </Dropdown>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="px-2 py-2 lg:p-6">
        {tableRows.length > 0 ? (
          viewMode === 'card' ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">{tableRows.map((player, index) => playerCard(player, index))}</div>
          ) : (
            <ColumnResizeTable columns={columns} dataSource={tableRows.map((player) => ({ ...player, key: player.id }))} />
          )
        ) : (
          createPlayersComponent()
        )}
      </div>

      {newPlayerModalOpen && (
        <Modal footer={null} open={newPlayerModalOpen} onCancel={() => setNewPlayerModalOpen(false)}>
          <Form layout="vertical" onFinish={(value) => debouncedFunction(() => postNewPlayer(value))} preserve={false}>
            <Form.Item label="编号" name="index" rules={[{ required: true, message: '请输入编号' }]} initialValue={`${maxPlayerIndex + 1}号`}>
              <Input />
            </Form.Item>
            <Form.Item label="姓名" name="name" rules={[{ required: true, message: '请输入姓名' }]}>
              <Input />
            </Form.Item>
            <Form.Item label="手机" name="mobile">
              <Input inputMode="numeric" />
            </Form.Item>
            <Form.Item label="团体" name="guild">
              <Select options={(match.guild_lists ?? []).map((guild, index) => ({ label: guild, value: guild, key: index }))} allowClear className="w-full" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                确定
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {editPlayerInfo && (
        <Modal footer={null} open={!!editPlayerInfo} onCancel={() => setEditPlayerInfo(undefined)}>
          <Form
            layout="vertical"
            requiredMark={(label, { required }) =>
              required ? (
                <span className="inline-flex items-center gap-1.5">
                  {label}
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
                </span>
              ) : label
            }
            onFinish={(value) => {
              putPlayer(value)
            }}
            initialValues={editPlayerInfo}
            preserve={false}
          >
            <Form.Item label="编号" name="index" rules={[{ required: true, message: '请填写编号' }]}>
              <Input />
            </Form.Item>
            <Form.Item label="是否离场" name="give_up" tooltip="开启=该选手退出后续赛程,一键编排/沿用上一轮自动跳过,过往成绩保留">
              <Switch defaultChecked={editPlayerInfo.give_up} />
            </Form.Item>
            <Form.Item label="是否签到" name="check_in" tooltip="开启后该选手视为已签到，可参与编排">
              <Switch defaultChecked={editPlayerInfo.check_in} />
            </Form.Item>
            <Form.Item label="姓名" name="name" rules={[{ required: true, message: '请填写姓名' }]}>
              <Input />
            </Form.Item>
            <Form.Item label="手机" name="mobile">
              <Input inputMode="numeric" />
            </Form.Item>
            <Form.Item label="团体" name="guild">
              <Select options={(match.guild_lists ?? []).map((guild, index) => ({ label: guild, value: guild, key: index }))} allowClear className="w-full" />
            </Form.Item>
            <Form.Item label="参与排名" name="rankable" tooltip="关闭后该选手不计入积分排名">
              <Switch defaultChecked={editPlayerInfo.rankable} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                确定
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {batchRenameModalOpen && (
        <Modal footer={null} open={batchRenameModalOpen} onCancel={() => setBatchRenameModalOpen(false)}>
          <Form
            layout="vertical"
            onFinish={(value) => {
              batchRename(value.start_number)
              setBatchRenameModalOpen(false)
            }}
            preserve={false}
          >
            <Form.Item label="起始" name="start_number" rules={[{ required: true, message: '请填写起始编号' }]} initialValue={1}>
              <InputNumber />
            </Form.Item>
            <Form.Item label="是否打乱" name="shuffle" initialValue={false}>
              <Radio.Group>
                <Radio value={false}>不打乱</Radio>
                <Radio value={true}>打乱</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                确定
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {batchSmsModal.open && (
        <Modal open={batchSmsModal.open} onCancel={() => setBatchSmsModal({ ...batchSmsModal, open: false })} footer={null}>
          <div className="flex flex-col items-center justify-center">
            <div className="my-5 mt-10 text-2xl font-bold">短信群发</div>
            <div className="mb-2 w-full font-semibold">短信内容：</div>
            <div className="w-full">
              尊敬的XXX先生/女士，您已成功报名：{' '}
              <Input
                placeholder="****大赛"
                className="w-32 underline"
                bordered={false}
                defaultValue={batchSmsModal.variables[0]}
                onChange={(e) => set(clone(batchSmsModal), ['variables', 0], e.target.value)}
              />
              ，系统自动默认您参加比赛，如有变动或调整请及时向赛委会提出申请，谢谢！公众号「灵缇互娱」 本次活动比赛将于
              <Input
                placeholder="**月**日 **:** - **:**"
                className="w-36 underline"
                bordered={false}
                defaultValue={batchSmsModal.variables[1]}
                onChange={(e) => set(clone(batchSmsModal), ['variables', 1], e.target.value)}
              />
              签到（过时未签到者视为自动放弃比赛，按实际情况为准），预祝您旗开得胜、掼出精彩！{' '}
              <Input
                placeholder="202*年*月*日"
                className="w-32 underline"
                bordered={false}
                defaultValue={batchSmsModal.variables[2]}
                onChange={(e) => set(clone(batchSmsModal), ['variables', 2], e.target.value)}
              />
            </div>
            <div className="mt-3 flex w-full items-start text-xs italic">XXX将替换为系统中参赛选手名称</div>
            <div className="mt-10 font-semibold">{`预计发送短信：${members.length}条`}</div>
            <div className="my-10">
              <Popover
                title="短信发送确认"
                trigger="click"
                content={
                  <div>
                    <div>{`本次将发送${members.length}条短信，一旦确认无法撤销`}</div>
                    <div className="mt-10 flex w-full flex-col items-center justify-center">
                      <Button
                        type="primary"
                        onClick={() => {
                          setBatchSmsModal({ ...batchSmsModal, loading: true })
                          const params = members.map((member) => ({
                            variables: [member.name ?? '', ...batchSmsModal.variables],
                            mobile: member.mobile,
                            user_id: `${member.mobile}-${member.name}-${random(0, 100000)}`,
                          }))
                          postBatchSms(params)
                        }}
                      >
                        确认发送
                      </Button>
                    </div>
                  </div>
                }
              >
                <Button loading={batchSmsModal.loading} type="primary">
                  立即发送
                </Button>
              </Popover>
            </div>
          </div>
        </Modal>
      )}
      {exportModalOpen && (
        <Modal open={exportModalOpen} onCancel={() => setExportModalOpen(false)} footer={false}>
          <div className="mt-4 text-center text-2xl font-semibold text-primary">导出队伍信息</div>
          <div>
            <div className="mb-6 text-lg font-semibold">导出表格</div>
            <Button
              className="h-8 w-40 rounded-lg border-2 border-gray-700 text-lg"
              onClick={() => {
                exportPlayerInfo()
                setExportModalOpen(false)
              }}
            >
              导出队伍信息表格
            </Button>
            <>
              <Divider />
              <div className="mb-6 text-lg font-semibold">导出文本</div>
              <Input.TextArea
                value={exportText}
                className="mb-6"
                autoSize={{
                  minRows: 5,
                  maxRows: 10,
                }}
              />
              <CopyToClipboard text={exportText} onCopy={() => message.info('复制成功')}>
                <Button className="h-8 w-40 rounded-lg border-2 border-gray-700 text-lg" onClick={() => {}}>
                  复制队伍信息文本
                </Button>
              </CopyToClipboard>
            </>
          </div>
        </Modal>
      )}
      {importModalOpen && (
        <Modal open={importModalOpen} onCancel={() => setImpoertModalOpen(false)} footer={false} loading={importLoading}>
          <div className="px-2 py-4">
            <div className="text-center text-2xl font-semibold text-blue-600">批量导入选手</div>
            <div className="mt-1 text-center text-sm text-gray-500">根据准备情况选择导入方式，几分钟完成全部选手录入</div>

            <div className="mt-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Excel 导入</div>
              <div className="mt-3 flex flex-col gap-4">
                {primaryExcelOption && renderExcelOptionCard(primaryExcelOption)}
                {secondaryExcelOptions.length > 0 && <div className="grid gap-4 md:grid-cols-2">{secondaryExcelOptions.map((option) => renderExcelOptionCard(option))}</div>}
              </div>
            </div>

            <Divider plain className="my-8 text-gray-400">
              或
            </Divider>

            <div>
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <FileTextOutlined className="text-blue-500" />
                <span>文本导入（高级）</span>
                <span className="text-red-400">(不建议)</span>
                <button
                  type="button"
                  className="ml-auto border-0 bg-transparent p-0 text-xs font-normal text-blue-600 underline underline-offset-2"
                  onClick={() => setTextImportExpanded((prev) => !prev)}
                >
                  {textImportExpanded ? '收起' : '展开'}
                </button>
              </div>
              {textImportExpanded ? (
                <div className="mt-3 rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm text-gray-600">每行代表一名选手：编号 + 姓名 + 手机号 + 团体，可用 Tab 分隔。</p>
                  <Input.TextArea
                    className="mt-4 rounded-xl border border-gray-200 bg-white"
                    autoSize={{
                      minRows: 5,
                    }}
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                    placeholder={'例：01号\t张三\t138****0000\t俱乐部'}
                  />
                  <div className="mt-4 flex flex-col gap-3 text-sm text-gray-500 md:flex-row md:items-center">
                    <Button
                      className="h-11 rounded-xl border-0 bg-brand px-8 text-lg font-semibold text-white hover:bg-brand-dark md:w-auto"
                      onClick={() => {
                        setImportLoading(true)
                        const playersText = importText.split('\n')
                        const newPlayers: playerType[] = playersText
                          .map((playerText, index) => {
                            const playerObject = playerText.split('\t')
                            const playerResult = {
                              index: playerObject[0] ?? `${index + 1}号`,
                              name: playerObject[1] ?? '',
                              mobile: playerObject[2] ?? '',
                              guild: playerObject[3],
                            } as playerType
                            return playerResult
                          })
                          .filter((t) => t.name)
                        if (newPlayers.length > 0) {
                          batchPostNewPlayer(newPlayers)
                        } else {
                          setImportLoading(false)
                          message.info('导入失败')
                        }
                      }}
                    >
                      从文本导入
                    </Button>
                    <div className="text-xs text-gray-400 md:text-left">支持直接从 Excel 粘贴，空行将被忽略。</div>
                  </div>
                </div>
              ) : (
                <div className="mt-3 rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  建议优先使用 Excel 导入，除非需要批量粘贴特殊文本格式。
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
      <Modal open={clubImportModalOpen} footer={false} onCancel={() => setClubImportModalOpen(false)}>
        <div className="mb-6 text-center text-lg">从俱乐部导入</div>
        <div className="flex justify-around">
          <Button onClick={() => importFromClubMembers(match.club)}>全体导入</Button>
          <Button type={clubMembersVisible ? 'primary' : 'default'} onClick={() => setClubMembersVisible((v) => !v)}>
            单个导入
          </Button>
        </div>
        {clubMembersVisible && (
          <div className="mt-2 h-[60vh] overflow-auto rounded-lg px-4 py-2 shadow">
            {club_members.map((member, index) => {
              const player = players.find((p) => p.mobile === member.mobile)
              return (
                <div key={member.id} className={`mb-1 flex justify-between rounded px-2 py-1 ${index % 2 === 1 && 'bg-gray-100'}`}>
                  <span>{member.name}</span>
                  <span>
                    {!player ? (
                      <Button size="small" type="primary" onClick={() => importOnePlayerFromClubMember(member.id)}>
                        导入
                      </Button>
                    ) : (
                      <Button size="small">已导入</Button>
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </Modal>
    </div>
  )
}

Players.getLayout = function getLayout(children: ReactElement) {
  return <ActionsLayout>{children}</ActionsLayout>
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  return {
    props: {
      match_id: ctx.query.match_id,
    },
  }
}

export default Players
